import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Avatar,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Settings,
  Chat,
  Email,
  Phone,
  Language,
  Facebook,
  Twitter,
  WhatsApp,
  Api,
  Visibility,
  VisibilityOff,
  Instagram,
  SmartToy
} from '@mui/icons-material';
import api, { inboxesAPI } from '../services/api';
import WhatsAppChannelConfig from '../components/WhatsAppChannelConfig';
import InstagramChannelConfig from '../components/InstagramChannelConfig';
import APIChannelConfig from '../components/APIChannelConfig';
import EmailChannelConfig from '../components/EmailChannelConfig';
import FacebookChannelConfig from '../components/FacebookChannelConfig';
import SMSChannelConfig from '../components/SMSChannelConfig';
import CustomGPTChannelConfig from '../components/CustomGPTChannelConfig';
import WebsiteChannelConfig from '../components/WebsiteChannelConfig';  // Added: 2025-11-19 - Website Widget Creation
import WidgetSettings from '../components/WidgetSettings';  // Added: 2025-11-18 - Website Widget Settings

interface Inbox {
  id: number;
  name: string;
  channel_type: string;
  account_id: number; // Account ID for API calls
  ai_enabled: boolean; // Channel-level AI toggle (default setting for new conversations)
  settings: Record<string, any>;
  description?: string;
  greeting_enabled: boolean;
  greeting_message?: string;
  working_hours_enabled: boolean;
  out_of_office_message?: string;
  auto_assignment_enabled: boolean;
  lock_to_single_conversation: boolean;
  // ai_enabled: boolean; 
  conversations_count: number;
  created_at: string;
  updated_at: string;
  api_channel_id?: number; // For API channels, contains the APIChannel.id
  email_channel_id?: number; // For Email channels, contains the EmailChannel.id
  whatsapp_channel_id?: number; // For WhatsApp channels, contains the WhatsAppChannel.id
  custom_gpt_channel_id?: number; // For Custom GPT channels, contains the CustomGPTChannel.id
}

interface InboxStats {
  total_conversations: number;
  open_conversations: number;
  pending_conversations: number;
  resolved_conversations: number;
  avg_first_response_time?: number;
  avg_resolution_time?: number;
}

const InboxesPage: React.FC = () => {
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInbox, setEditingInbox] = useState<Inbox | null>(null);
  const [statsOpen, setStatsOpen] = useState(false);
  const [selectedInboxStats, setSelectedInboxStats] = useState<InboxStats | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [showWhatsAppConfig, setShowWhatsAppConfig] = useState(false);
  const [showInstagramConfig, setShowInstagramConfig] = useState(false);
  const [showAPIConfig, setShowAPIConfig] = useState(false);
  const [showEmailConfig, setShowEmailConfig] = useState(false);
  const [showFacebookConfig, setShowFacebookConfig] = useState(false);
  const [showSMSConfig, setShowSMSConfig] = useState(false);
  const [showCustomGPTConfig, setShowCustomGPTConfig] = useState(false);
  const [showWebsiteConfig, setShowWebsiteConfig] = useState(false);  // Added: 2025-11-19 - Website Widget Creation
  const [showWidgetSettings, setShowWidgetSettings] = useState(false);  // Added: 2025-11-18 - Widget Settings
  const [selectedInboxForWidget, setSelectedInboxForWidget] = useState<Inbox | null>(null);
  const [selectedChannelType, setSelectedChannelType] = useState<string>('');


  const [formData, setFormData] = useState({
    name: '',
    channel_type: 'website',
    description: '',
    greeting_enabled: true,
    greeting_message: 'Hello! How can we help you today?',
    working_hours_enabled: false,
    out_of_office_message: '',
    auto_assignment_enabled: true,
    lock_to_single_conversation: false,
    ai_enabled: true, // Channel-level AI toggle (default setting for new conversations)
    settings: {}
  });

  const channelTypes = [
    { value: 'website', label: 'Website Widget', icon: <Chat /> },
    { value: 'api', label: 'API Channel', icon: <Api /> },
    { value: 'email', label: 'Email', icon: <Email /> },
    { value: 'whatsapp', label: 'WhatsApp', icon: <WhatsApp /> },
    { value: 'facebook', label: 'Facebook', icon: <Facebook /> },
    { value: 'twitter', label: 'Twitter', icon: <Twitter /> },
    { value: 'instagram', label: 'Instagram', icon: <Instagram /> },
    { value: 'sms', label: 'SMS', icon: <Phone /> },
    { value: 'custom_gpt', label: 'Custom GPT', icon: <SmartToy /> }
  ];

  const getChannelIcon = (channelType: string) => {
    const channel = channelTypes.find(ct => ct.value === channelType);
    return channel?.icon || <Chat />;
  };

  const getChannelLabel = (channelType: string) => {
    const channel = channelTypes.find(ct => ct.value === channelType);
    return channel?.label || channelType;
  };

  const loadInboxes = async () => {
    try {
      setLoading(true);
      const response = await inboxesAPI.getInboxes();
      setInboxes(response.inboxes);
    } catch (error) {
      console.error('Failed to load inboxes:', error);
      setSnackbar({ open: true, message: 'Failed to load inboxes', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInbox = async () => {
    try {
      const response = await inboxesAPI.createInbox(formData);
      setInboxes([...inboxes, response]);
      setDialogOpen(false);
      resetForm();
      setSnackbar({ open: true, message: 'Inbox created successfully', severity: 'success' });

      // For website channels, automatically open widget settings after creation
      if (formData.channel_type === 'website') {
        setSelectedInboxForWidget(response);
        setShowWidgetSettings(true);
      }
    } catch (error) {
      console.error('Failed to create inbox:', error);
      setSnackbar({ open: true, message: 'Failed to create inbox', severity: 'error' });
    }
  };

  const handleUpdateInbox = async () => {
    if (!editingInbox) return;

    try {
      const response = await inboxesAPI.updateInbox(editingInbox.id, formData);
      setInboxes(inboxes.map(inbox => inbox.id === editingInbox.id ? response : inbox));
      setDialogOpen(false);
      setEditingInbox(null);
      resetForm();
      setSnackbar({ open: true, message: 'Inbox updated successfully', severity: 'success' });
    } catch (error) {
      console.error('Failed to update inbox:', error);
      setSnackbar({ open: true, message: 'Failed to update inbox', severity: 'error' });
    }
  };

  const handleDeleteInbox = async (inboxId: number) => {
    if (!window.confirm('Are you sure you want to delete this inbox?')) return;

    try {
      await inboxesAPI.deleteInbox(inboxId);
      setInboxes(inboxes.filter(inbox => inbox.id !== inboxId));
      setSnackbar({ open: true, message: 'Inbox deleted successfully', severity: 'success' });
    } catch (error) {
      console.error('Failed to delete inbox:', error);
      setSnackbar({ open: true, message: 'Failed to delete inbox', severity: 'error' });
    }
  };

  const handleViewStats = async (inboxId: number) => {
    try {
      const response = await inboxesAPI.getInboxStats(inboxId);
      setSelectedInboxStats(response);
      setStatsOpen(true);
    } catch (error) {
      console.error('Failed to load inbox stats:', error);
      setSnackbar({ open: true, message: 'Failed to load inbox stats', severity: 'error' });
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setEditingInbox(null);
    setSelectedChannelType('');
    setDialogOpen(true);
  };

  const handleChannelTypeSelect = (channelType: string) => {
    setDialogOpen(false);
    setSelectedChannelType(channelType);

    // Show appropriate configuration component
    switch (channelType) {
      case 'website':
        setShowWebsiteConfig(true);
        break;
      case 'whatsapp':
        setShowWhatsAppConfig(true);
        break;
      case 'instagram':
        setShowInstagramConfig(true);
        break;
      case 'api':
      case 'API':
        console.log('🔍 DEBUG: Matched API case, setting showAPIConfig to true');
        setShowAPIConfig(true);
        break;
      case 'email':
        setShowEmailConfig(true);
        break;
      case 'facebook':
        setShowFacebookConfig(true);
        break;
      case 'sms':
        setShowSMSConfig(true);
        break;
      case 'custom_gpt':
        setShowCustomGPTConfig(true);
        break;
      default:
        // For other channel types, keep existing behavior
        setFormData(prev => ({ ...prev, channel_type: channelType }));
        setDialogOpen(true);
        break;
    }
  };

  const handleChannelCreated = (channel: any, channelType: string) => {
    setInboxes(prev => [...prev, channel]);
    closeAllConfigs();
    setSnackbar({ open: true, message: `${channelType.charAt(0).toUpperCase() + channelType.slice(1)} channel created successfully`, severity: 'success' });
    loadInboxes(); // Reload to get updated data
  };

  const closeAllConfigs = () => {
    setShowWhatsAppConfig(false);
    setShowInstagramConfig(false);
    setShowAPIConfig(false);
    setShowEmailConfig(false);
    setShowCustomGPTConfig(false);
    setShowFacebookConfig(false);
    setShowSMSConfig(false);
    setShowWebsiteConfig(false);  // Added: 2025-11-19
    setShowWidgetSettings(false);  // Added: 2025-11-18
    setEditingInbox(null);
    setSelectedInboxForWidget(null);
  };

  // Individual channel creation handlers
  const handleWebsiteChannelCreated = (channel: any) => handleChannelCreated(channel, 'website');
  const handleWhatsAppChannelCreated = (channel: any) => handleChannelCreated(channel, 'whatsapp');
  const handleInstagramChannelCreated = (channel: any) => handleChannelCreated(channel, 'instagram');
  const handleAPIChannelCreated = (channel: any) => handleChannelCreated(channel, 'API');
  const handleEmailChannelCreated = (channel: any) => handleChannelCreated(channel, 'email');
  const handleFacebookChannelCreated = (channel: any) => handleChannelCreated(channel, 'facebook');
  const handleSMSChannelCreated = (channel: any) => handleChannelCreated(channel, 'SMS');
  const handleCustomGPTChannelCreated = (channel: any) => handleChannelCreated(channel, 'custom_gpt');

  const openEditDialog = (inbox: Inbox) => {
    console.log('🔍 DEBUG: Opening edit dialog for inbox:', inbox);
    console.log('🔍 DEBUG: API channel ID:', inbox.api_channel_id);
    console.log('🔍 DEBUG: Channel type:', inbox.channel_type, 'Type:', typeof inbox.channel_type);
    setEditingInbox(inbox);
    
    switch (inbox.channel_type) {
      case 'whatsapp':
        setShowWhatsAppConfig(true);
        break;
      case 'instagram':
        setShowInstagramConfig(true);
        break;
      case 'api':
      case 'API':
        console.log('🔍 DEBUG: Matched API case, setting showAPIConfig to true');
        setShowAPIConfig(true);
        break;
      case 'email':
        setShowEmailConfig(true);
        break;
      case 'facebook':
        setShowFacebookConfig(true);
        break;
      case 'sms':
        setShowSMSConfig(true);
        break;
      case 'custom_gpt':
        setShowCustomGPTConfig(true);
        break;
      default:
        // For basic channel types like website
        console.log('🔍 DEBUG: No specific case matched, using default dialog for channel type:', inbox.channel_type);
        setFormData({
          name: inbox.name,
          channel_type: inbox.channel_type,
          description: inbox.description || '',
          greeting_enabled: inbox.greeting_enabled,
          greeting_message: inbox.greeting_message || '',
          working_hours_enabled: inbox.working_hours_enabled,
          out_of_office_message: inbox.out_of_office_message || '',
          auto_assignment_enabled: inbox.auto_assignment_enabled,
          lock_to_single_conversation: inbox.lock_to_single_conversation,
          ai_enabled: inbox.ai_enabled ?? true,  // Channel-level AI toggle
          settings: inbox.settings || {}
        });
        setEditingInbox(inbox);
        setDialogOpen(true);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      channel_type: 'website',
      description: '',
      greeting_enabled: true,
      greeting_message: 'Hello! How can we help you today?',
      working_hours_enabled: false,
      out_of_office_message: '',
      auto_assignment_enabled: true,
      lock_to_single_conversation: false,
      ai_enabled: true,  // Channel-level AI toggle (default: enabled)
      settings: {}
    });
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleToggleAI = async (inboxId: number, currentValue: boolean) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api/v1/inboxes/${inboxId}/toggle-ai`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ai_enabled: !currentValue })
      });
  
      if (response.ok) {
        const result = await response.json();
        // Update local state
        setInboxes(prev => prev.map(inbox =>
          inbox.id === inboxId
            ? { ...inbox, ai_enabled: result.ai_enabled }
            : inbox
        ));
        // Show success message (optional)
        console.log(`AI ${result.ai_enabled ? 'enabled' : 'disabled'} for inbox ${inboxId}`);
      } else {
        console.error('Failed to toggle AI');
      }
    } catch (error) {
      console.error('Error toggling AI:', error);
    }
  };

  useEffect(() => {
    loadInboxes();
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading inboxes...</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Inboxes
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreateDialog}
        >
          Create Inbox
        </Button>
      </Box>

      {/* Inbox Overview Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                Total Inboxes
              </Typography>
              <Typography variant="h4">
                {inboxes?.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                Total Conversations
              </Typography>
              <Typography variant="h4">
                {inboxes?.reduce((sum, inbox) => sum + inbox.conversations_count, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                Active Channels
              </Typography>
              <Typography variant="h4">
                {new Set(inboxes?.map(inbox => inbox.channel_type)).size}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">
                Auto Assignment
              </Typography>
              <Typography variant="h4">
                {inboxes?.filter(inbox => inbox.auto_assignment_enabled).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Inboxes Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Channel Type</TableCell>
                <TableCell>Conversations</TableCell>
                <TableCell align="center">AI Enabled</TableCell>
                <TableCell>Settings</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inboxes.map((inbox) => (
                <TableRow key={inbox.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                        {getChannelIcon(inbox.channel_type)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {inbox.name}
                        </Typography>
                        {inbox.description && (
                          <Typography variant="caption" color="text.secondary">
                            {inbox.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getChannelLabel(inbox.channel_type)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {inbox.conversations_count} conversations
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={inbox.ai_enabled ?? false}
                      onChange={() => handleToggleAI(inbox.id, inbox.ai_enabled ?? false)}
                      color="primary"
                      size="small"
                      title={inbox.ai_enabled ? 'Disable AI for this channel' : 'Enable AI for this channel'}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1} flexWrap="wrap">
                      {/* {inbox.ai_enabled && (
                        <Chip label="AI" size="small" color="info" icon={<SmartToy sx={{ fontSize: 16 }} />} />
                      )} */}
                      {inbox.greeting_enabled && (
                        <Chip label="Greeting" size="small" color="primary" />
                      )}
                      {inbox.working_hours_enabled && (
                        <Chip label="Working Hours" size="small" color="secondary" />
                      )}
                      {inbox.auto_assignment_enabled && (
                        <Chip label="Auto Assign" size="small" color="success" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label="Active"
                      size="small"
                      color="success"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleViewStats(inbox.id)}
                      title="View Statistics"
                    >
                      <Visibility />
                    </IconButton>
                    {/* For website channels, show Settings icon; for others, show Edit icon */}
                    {inbox.channel_type === 'website' ? (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedInboxForWidget(inbox);
                          setShowWidgetSettings(true);
                        }}
                        title="Widget Settings"
                        color="primary"
                      >
                        <Settings />
                      </IconButton>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(inbox)}
                        title="Edit Inbox"
                      >
                        <Edit />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteInbox(inbox.id)}
                      title="Delete Inbox"
                      color="error"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingInbox ? 'Edit Inbox' : 'Create New Inbox'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={3} mt={2}>
            {!selectedChannelType && !editingInbox ? (
              <>
                <Typography variant="h6" gutterBottom>
                  Select Channel Type
                </Typography>
                <Grid container spacing={2}>
                  {channelTypes.map((type) => (
                    <Grid item xs={12} sm={6} md={4} key={type.value}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                        onClick={() => handleChannelTypeSelect(type.value)}
                      >
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Box sx={{ mb: 1 }}>
                            {React.cloneElement(type.icon, { fontSize: 'large', color: 'primary' })}
                          </Box>
                          <Typography variant="subtitle1">
                            {type.label}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            ) : (
              <>
                <TextField
                  label="Inbox Name"
                  value={formData.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                  fullWidth
                  required
                />
                
                <FormControl fullWidth>
                  <InputLabel>Channel Type</InputLabel>
                  <Select
                    value={formData.channel_type}
                    onChange={(e) => handleFormChange('channel_type', e.target.value)}
                    label="Channel Type"
                    disabled={!!editingInbox}
                  >
                    {channelTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {type.icon}
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}

            {((selectedChannelType && selectedChannelType !== 'whatsapp') || (editingInbox && editingInbox.channel_type !== 'whatsapp')) && (
              <>

            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              fullWidth
              multiline
              rows={2}
            />

            <Divider />
            
            <Typography variant="h6">Settings</Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.greeting_enabled}
                  onChange={(e) => handleFormChange('greeting_enabled', e.target.checked)}
                />
              }
              label="Enable Greeting Message"
            />

            {formData.greeting_enabled && (
              <TextField
                label="Greeting Message"
                value={formData.greeting_message}
                onChange={(e) => handleFormChange('greeting_message', e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.working_hours_enabled}
                  onChange={(e) => handleFormChange('working_hours_enabled', e.target.checked)}
                />
              }
              label="Enable Working Hours"
            />

            {formData.working_hours_enabled && (
              <TextField
                label="Out of Office Message"
                value={formData.out_of_office_message}
                onChange={(e) => handleFormChange('out_of_office_message', e.target.value)}
                fullWidth
                multiline
                rows={2}
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={formData.auto_assignment_enabled}
                  onChange={(e) => handleFormChange('auto_assignment_enabled', e.target.checked)}
                />
              }
              label="Enable Auto Assignment"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.lock_to_single_conversation}
                  onChange={(e) => handleFormChange('lock_to_single_conversation', e.target.checked)}
                />
              }
              label="Lock to Single Conversation"
            />

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6">AI Settings</Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.ai_enabled}
                  onChange={(e) => handleFormChange('ai_enabled', e.target.checked)}
                />
              }
              label="Enable AI Responses"
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mt: -1 }}>
              When enabled, new conversations will have AI responses turned on by default.
              Agents can still manually enable/disable AI for individual conversations.
            </Typography>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={editingInbox ? handleUpdateInbox : handleCreateInbox}
            variant="contained"
          >
            {editingInbox ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stats Dialog */}
      <Dialog open={statsOpen} onClose={() => setStatsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Inbox Statistics</DialogTitle>
        <DialogContent>
          {selectedInboxStats && (
            <Grid container spacing={2} mt={1}>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary">
                      Total
                    </Typography>
                    <Typography variant="h4">
                      {selectedInboxStats.total_conversations}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="success.main">
                      Open
                    </Typography>
                    <Typography variant="h4">
                      {selectedInboxStats.open_conversations}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="warning.main">
                      Pending
                    </Typography>
                    <Typography variant="h4">
                      {selectedInboxStats.pending_conversations}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="info.main">
                      Resolved
                    </Typography>
                    <Typography variant="h4">
                      {selectedInboxStats.resolved_conversations}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Multi-Provider Channel Configurations */}

      {/* Website Channel Configuration */}
      {showWebsiteConfig && (
        <Dialog open={true} onClose={closeAllConfigs} maxWidth="xl" fullWidth>
          <DialogContent>
            <WebsiteChannelConfig
              onChannelCreated={handleWebsiteChannelCreated}
              onClose={closeAllConfigs}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* WhatsApp Channel Configuration */}
      {showWhatsAppConfig && (
        <Dialog open={true} onClose={closeAllConfigs} maxWidth="lg" fullWidth>
          <DialogContent>
            <WhatsAppChannelConfig
              onChannelCreated={handleWhatsAppChannelCreated}
              editingChannel={editingInbox}
              onCancel={closeAllConfigs}
            />
          </DialogContent>
        </Dialog>
      )}
      
      {/* Instagram Channel Configuration */}
      {showInstagramConfig && (
        <Dialog open={true} onClose={closeAllConfigs} maxWidth="lg" fullWidth>
          <DialogContent>
            <InstagramChannelConfig
              editingChannel={editingInbox}
              onCancel={closeAllConfigs}
            />
          </DialogContent>
        </Dialog>
      )}
      
      {/* API Channel Configuration */}
      {showAPIConfig && (
        <Dialog open={true} onClose={closeAllConfigs} maxWidth="lg" fullWidth>
          <DialogContent>
            <APIChannelConfig
              editMode={!!editingInbox}
              channelId={editingInbox?.api_channel_id}
              fallbackChannelId={editingInbox?.id}
              onSave={(channelData) => {
                console.log('API Channel saved:', channelData);
                loadInboxes();
                closeAllConfigs();
              }}
              onCancel={closeAllConfigs}
            />
          </DialogContent>
        </Dialog>
      )}
      
      {/* Email Channel Configuration */}
      {showEmailConfig && (
        <Dialog open={true} onClose={closeAllConfigs} maxWidth="lg" fullWidth>
          <DialogContent>
            <EmailChannelConfig
              channelId={editingInbox?.email_channel_id}
              onClose={closeAllConfigs}
            />
          </DialogContent>
        </Dialog>
      )}
      
      {/* Facebook Channel Configuration */}
      {showFacebookConfig && (
        <Dialog open={true} onClose={closeAllConfigs} maxWidth="lg" fullWidth>
          <DialogContent>
            <FacebookChannelConfig
              editingChannel={editingInbox}
              onCancel={closeAllConfigs}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* SMS Channel Configuration */}
      {showSMSConfig && (
        <Dialog open={true} onClose={closeAllConfigs} maxWidth="lg" fullWidth>
          <DialogContent>
            <SMSChannelConfig
              editingChannel={editingInbox}
              onCancel={closeAllConfigs}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Custom GPT Channel Configuration */}
      {showCustomGPTConfig && (
        <Dialog open={true} onClose={closeAllConfigs} maxWidth="lg" fullWidth>
          <DialogContent>
            <CustomGPTChannelConfig
              onChannelCreated={handleCustomGPTChannelCreated}
              editingChannel={editingInbox}
              onCancel={closeAllConfigs}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Widget Settings - For website channels */}
      {showWidgetSettings && selectedInboxForWidget && (
        <Dialog open={true} onClose={closeAllConfigs} maxWidth="xl" fullWidth>
          <DialogTitle>
            Widget Settings - {selectedInboxForWidget.name}
          </DialogTitle>
          <DialogContent>
            <WidgetSettings inboxId={selectedInboxForWidget.id} onClose={closeAllConfigs} />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAllConfigs}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default InboxesPage;